<template>
  <div class="ai-agent-container" style="position: relative; height: 100%; padding: 10px">
    <div style="padding: 20px; padding-bottom: 20px">
      <el-button
        type="warning"
        @click="handleCreate"
        circle
        icon="el-icon-plus"
        :disabled="listLoading"
      ></el-button>
    </div>

    <div style="position: relative; height: 80%">
      <el-table
        v-loading="listLoading"
        size="mini"
        :data="list"
        border
        fit
        highlight-current-row
        height="100%"
      >
        <el-table-column align="center" label="項次" width="60">
          <template slot-scope="scope">
            <span>{{ scope.$index + 1 }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="使用功能名稱" min-width="200">
          <template slot-scope="scope">
            <span>{{ scope.row.functionName }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="目前選用模型" min-width="150">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.useModel" size="medium">{{ scope.row.useModel }}</el-tag>
            <span v-else style="color: #909399">尚未設定</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="操作" width="160">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)">設定</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">刪除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <pagination
      v-show="total > 0"
      style="padding-top: 0"
      layout="prev, pager, next"
      :total="total"
      :page.sync="listQuery.page"
      :limit.sync="listQuery.limit"
      @pagination="getList"
    />

    <el-dialog
      v-loading="dialogLoading"
      :top="dialogTopMargin"
      :title="'模型設定設定 - ' + (form.functionName || '新功能')"
      :visible.sync="dialogVisible"
      width="50%"
    >
      <component
        ref="currentForm"
        :is="currentFormComp"
        v-if="dialogVisible"
        :form-data="form"
        @set-loading="val => (dialogLoading = val)"
      />

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveBasicData">確 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import { Message } from 'element-ui'
import OpenAiBaseForm from '@/components/Form/OpenAiBaseForm'
import {
  saveOpenAIModelSetting,
  getOpenAIModelSetting,
  deleteOpenAIModelSetting,
} from '@/api/openAI'

function createDefaultForm() {
  return {
    id: null,
    aiBrand: 'OpenAI',
    project: 'ita.GroupTravel.Agency.API',
    functionCode: '',
    functionName: '',
    useModel: 'gpt-4o-mini',
    functionTypes: [],
    prompt: '',
    outputResolution: undefined,
    convertDpi: undefined,
    formType: 'OpenAiBaseForm',
  }
}

function cloneDeep(value) {
  return JSON.parse(JSON.stringify(value))
}

export default {
  name: 'AiAgentManagement',
  components: {
    Pagination,
    OpenAiBaseForm, // 註冊動態表單組件
  },
  data() {
    return {
      funcName: 'AI Agent',
      dialogTopMargin: '5%',
      form: createDefaultForm(),
      dialogLoading: false,
      dialogVisible: false,
      listLoading: false,
      listQuery: {
        page: 1,
        limit: 20,
      },
      total: 0,
      list: [],
      currentFormComp: 'OpenAiBaseForm',
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      this.listLoading = true
      try {
        const response = await getOpenAIModelSetting(this.listQuery)
        if (response.success) {
          this.list = response.data
          this.total = response.total || response.data.length || 0
        } else {
          Message.error(response.message || '獲取資料失敗')
        }
      } catch (error) {
        Message.error((error.response && error.response.message) || '獲取資料失敗')
      }
      this.listLoading = false
    },
    handleCreate() {
      this.form = createDefaultForm()
      this.currentFormComp = 'OpenAiBaseForm'
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.form = cloneDeep(row)

      // 動態判定要使用哪一個表單。若該列資料有指定 formType，就用它的，否則預設 OpenAiBaseForm
      this.currentFormComp = row.formType || 'OpenAiBaseForm'
      this.dialogVisible = true
    },
    async handleDelete(row) {
      let confirmMes = '確定要刪除AI模型設定嗎?'

      if (this.form && this.form.id && this.form.id > 0) {
        if (this.form.functionName) {
          confirmMes = `確定要刪除${this.form.functionName}AI模型設定嗎?`
        } else {
          confirmMes = `確定要刪除AI模型設定嗎?`
        }
      }

      if (!confirm(confirmMes)) {
        return
      }

      try {
        await this.$store.dispatch(
          'app/showAIProcessLoadingText',
          '正在處理中，請不要離開此畫面，感謝您的耐心等候。'
        )
        await this.$store.dispatch('app/showAIProcessLoading', true)
        this.dialogLoading = true

        if (row.id && row.id > 0) {
          const response = await deleteOpenAIModelSetting(row.id)

          if (response.success) {
            Message.success(response.message || '刪除成功')
            this.dialogVisible = false
            this.getList() // 重新刷新列表
          } else {
            Message.error(response.message || '刪除失敗')
          }
        }

        this.dialogLoading = false
        await this.$store.dispatch('app/showAIProcessLoading', false)
      } catch (error) {
        console.log('刪除設定失敗', error)
        this.dialogLoading = false
        await this.$store.dispatch('app/showAIProcessLoading', false)
      }
    },

    // 儲存表單資料
    async saveBasicData() {
      let confirmMes = '確定要新增AI模型設定嗎?'

      if (this.form && this.form.id && this.form.id > 0) {
        if (this.form.functionName) {
          confirmMes = `確定要保存${this.form.functionName}AI模型設定嗎?`
        } else {
          confirmMes = `確定要保存AI模型設定嗎?`
        }
      }

      if (!confirm(confirmMes)) {
        return
      }

      const currentForm = this.$refs['currentForm']
      const isValid = currentForm ? await currentForm.validateForm() : false

      if (!isValid) {
        this.$message.warning('請檢查欄位是否填寫正確！')
        return
      }

      const { formType, id, ...cleanForm } = this.form
      const payload = {
        ...cleanForm,
        id: id && id > 0 ? id : 0,
      }

      try {
        await this.$store.dispatch(
          'app/showAIProcessLoadingText',
          '正在處理中，請不要離開此畫面，感謝您的耐心等候。'
        )
        await this.$store.dispatch('app/showAIProcessLoading', true)
        this.dialogLoading = true

        const response = await saveOpenAIModelSetting(payload)

        if (response.success) {
          Message.success(response.message || '儲存成功')
          this.dialogVisible = false
          this.getList() // 重新刷新列表
        } else {
          Message.error(response.message || '儲存失敗')
        }
      } catch (error) {
        Message.error((error.response && error.response.message) || '儲存失敗')
      } finally {
        this.dialogLoading = false
        await this.$store.dispatch('app/showAIProcessLoading', false)
      }
    },
  },
}
</script>

<style scoped>
.ai-agent-container .el-dialog__body {
  padding-top: 0px;
}
</style>
